import React, { FunctionComponent, useState, useEffect } from "react";
import {
  DefaultButton,
  IconButton,
  Modal,
  PrimaryButton,
  TextField,
  Toggle,
} from "@fluentui/react";
import { CustomFontText } from "components/customFontText/CustomFontText";
import { Stack } from "@fluentui/react/lib/Stack";
import { Dropdown } from "@fluentui/react/lib/Dropdown";
import {
  iconButtonStyles,
  cancelIcon,
  contentStyles as contentStyle,
  colourGrey,
  colourDefault,
} from "constants/styles";
import {
  dealStatuses,
  dealTypes,
  FavouriteAction,
  ProspectType,
} from "constants/constants";
import ListItem from "components/ListItem/ListItem";
import { EditProspectDialog } from "components/dialogs/prospects/EditProspectDialog";
import { CosignersDialog } from "components/dialogs/deals/CosignersDialog";
import AddProspectToDealDialog from "components/dialogs/deals/AddProspectToDealDialog";
import AddVehicleToDealDialog from "components/dialogs/deals/AddVehicleToDealDialog";
import { EditInventoryDialog } from "components/dialogs/editInventoryDialog/EditInventoryDialog";
import { Spinner, SpinnerSize } from "@fluentui/react/lib/Spinner";
import { AppState } from "store";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { IProspect } from "types/prospectTypes";
import { IInventory } from "types/inventoryTypes";
import { IDeal } from "types/dealTypes";
import { DealEditAdditionalActionsDropdown } from "components/dialogs/deals/dropdown/DealEditAdditionalActionsDropdown";
import { getModulePermissions } from "utils/permissions/permissionsHelpers";
import { handleUpdateDeal } from "store/deals/deals.action";
import { handleUpdateFavourite } from "store/shared/shared.action";
import { ConfirmDialog } from "../confirmDialog/ConfirmDialog";
import { handleDeleteDeal } from "store/deals/deals.action";
import moment from "moment";
import { EditCosignerDialog } from "components/dialogs/deals/EditCosignerDialog";
import { EditProspectFromCosignersDialog } from "components/dialogs/deals/EditProspectFromCosignersDialog";
// import { ICosigner } from "../../../types/cosignerTypes";

const contentStyles = contentStyle(600, "auto");

enum FormStep {
  None,
  EditProspect,
  ChangeAssignedProspect,
  AssignVehicleToDeal,
  Cosigners,
  EditInventory,
  EditCosignerFromCosigners,
  EditProspectFromCosigners,
}

interface IProps {
  onDismiss: () => void;
  /**
   * The prospect object from which Deal must be created
   * */
  selectedDeal: IDeal;
}

export const EditDealDialog: FunctionComponent<IProps> = ({
  onDismiss,
  selectedDeal,
}) => {
  const loading = useSelector((state: AppState) => state.deals.loading);
  const [isConfirmBoxOpen, setIsConfirmBoxOpen] = useState(false);
  const user = useSelector((state: AppState) => state.userProfile.userProfile);
  const [selectedProspect, setSelectedProspect] = useState<IProspect | null>(
    null
  );

  const [index, setIndex] = useState(null);
  const [stepForm, setStepForm] = React.useState(FormStep.None);

  const [selectedType, setSelectedType] = useState(1);

  const [defaultCosigners, setDefaultCosigners] = useState([]);

  const [selectedInventory, setSelectedInventory] = useState<IInventory | null>(
    null
  );
  // const [selectedCosigners, setSelectedCosigners] = useState<any[] | null>(
  //   null
  // ); /*@TODO: change type to Cosigner*/

  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedDeal) {
      setDefaultCosigners(selectedDeal.signers);

      formik.setValues({
        updatedAt: selectedDeal.updatedAt,
        createdAt: selectedDeal.createdAt,
        setDealType: selectedDeal.type,
        dealStatus: selectedDeal.status,
        hasOfferBeenSent: selectedDeal.isSent,
        assignedProspect: selectedDeal.prospect,
        assignedVehicle: selectedDeal.inventory,
        cosigners:
          selectedDeal.signers && selectedDeal.signers.length > 0
            ? [...selectedDeal.signers]
            : [null, null],
      });
      setSelectedProspect({ ...selectedDeal.prospect });
      setSelectedInventory({ ...selectedDeal.inventory });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeal]);

  const formik = useFormik({
    initialValues: {
      updatedAt: "",
      createdAt: "",
      setDealType: null,
      dealStatus: "",
      hasOfferBeenSent: false,
      assignedProspect: null,
      assignedVehicle: null,
      cosigners: [null, null],
    },
    validationSchema: Yup.object({
      setDealType: Yup.number().required("Required"),
      dealStatus: Yup.string().required("Required"),
      hasOfferBeenSent: Yup.boolean(),
      assignedProspect: Yup.object().required("Required"),
      assignedVehicle: Yup.object().required("Required"),
      updatedAt: Yup.string(),
      createdAt: Yup.string(),
      cosigners: Yup.array().of(Yup.object().nullable()),
    }),
    onSubmit: (values) => {
      let cosignersToAssign = [];
      const cosignersToUnassigned = [];

      const cosignersToStay = [];

      if (defaultCosigners.length === 0) {
        cosignersToAssign = values.cosigners;
      } else {
        for (let i = 0; i < defaultCosigners.length; i++) {
          if (
            Boolean(
              values.cosigners.find(
                (item) => item && item.id === defaultCosigners[i].id
              )
            )
          ) {
            cosignersToStay.push(defaultCosigners[i]);
          } else {
            cosignersToUnassigned.push(defaultCosigners[i]);
          }
        }

        for (let i = 0; i < cosignersToStay.length; i++) {
          cosignersToAssign = values.cosigners.filter(
            (item) => item && item.id !== cosignersToStay[i].id
          );
        }
      }

      const dealToSend: any = {
        id: selectedDeal.id,
        type: +values.setDealType,
        status: values.dealStatus,
        isSent: values.hasOfferBeenSent,
        prospect: selectedProspect ? selectedProspect : null,
        inventory: selectedInventory ? selectedInventory : null,
        isFavourite: selectedDeal.isFavourite,
        createdAt: selectedDeal.createdAt,
        updatedAt: selectedDeal.updatedAt,
        signers: values.cosigners,
        cosignersToAssign: cosignersToAssign,
        cosignersToUnassigned: cosignersToUnassigned,
      };

      dispatch<any>(
        handleUpdateDeal([dealToSend], () => {
          onDismiss();
        })
      );
    },
  });

  const handleUpdate = () => {
    formik.handleSubmit();
  };

  const updateProspect = (prospect: IProspect) => {
    setSelectedProspect(prospect);
    setStepForm(FormStep.None);
    formik.setFieldValue("assignedProspect", prospect.id);
  };

  const updateInventory = (inventory: IInventory) => {
    setSelectedInventory(inventory);
    setStepForm(FormStep.None);
    formik.setFieldValue("assignedInventory", inventory.id);
  };

  // const updateCosigners = (cosigners: IProspect[]) => {
  //   // setSelectedCosigners([...cosigners]);
  //  };

  const renderSpinner = () => {
    return loading ? <Spinner styles={{}} size={SpinnerSize.medium} /> : null;
  };

  const _renderDialog = () => {
    switch (stepForm) {
      case FormStep.None:
        return null;
      case FormStep.EditProspect:
        return (
          <EditProspectDialog
            onDismiss={() => setStepForm(FormStep.None)}
            prospect={selectedProspect ? selectedProspect : null}
            returnProspect={(prospect: IProspect) => updateProspect(prospect)}
          />
        );
      case FormStep.EditInventory:
        return (
          <EditInventoryDialog
            onDismiss={() => setStepForm(FormStep.None)}
            selectedInventory={selectedInventory ? selectedInventory : null}
            returnInventory={(inventory: IInventory) =>
              updateInventory(inventory)
            }
          />
        );

      case FormStep.ChangeAssignedProspect:
        return (
          <AddProspectToDealDialog
            onDismiss={() => setStepForm(FormStep.None)}
            onProspectSelection={(prospect: IProspect) => {
              updateProspect(prospect);
            }}
            selectedProspect={selectedProspect ? selectedProspect : null}
            type={selectedType}
          />
        );
      case FormStep.AssignVehicleToDeal:
        return (
          <AddVehicleToDealDialog
            onDismiss={() => setStepForm(FormStep.None)}
            onInventorySelection={(inventory: IInventory) => {
              // console.log(inventory);
              setSelectedInventory(inventory);
              formik.setFieldValue("assignedVehicle", inventory.id);
              setStepForm(FormStep.None);
            }}
            selectedInventory={selectedInventory ? selectedInventory : null}
          />
        );

      case FormStep.EditCosignerFromCosigners:
        return (
          <EditCosignerDialog
            index={index}
            cosigners={formik.values.cosigners}
            onDismiss={() => setStepForm(FormStep.Cosigners)}
            setCosigner={(cosigner) => {
              formik.setFieldValue(
                "cosigners",
                index === 0
                  ? [cosigner, formik.values.cosigners[1]]
                  : [formik.values.cosigners[0], cosigner]
              );
            }}
          />
        );
      case FormStep.EditProspectFromCosigners:
        return (
          <EditProspectFromCosignersDialog
            onDismiss={() => setStepForm(FormStep.Cosigners)}
            prospect={formik.values.cosigners[index]}
            returnProspect={(prospect) => {
              formik.setFieldValue(
                "cosigners",
                index === 0
                  ? [prospect, formik.values.cosigners[1]]
                  : [formik.values.cosigners[0], prospect]
              );
            }}
          />
        );

      case FormStep.Cosigners:
        return (
          <CosignersDialog
            formik={formik}
            onShowAddFromProspect={() =>
              setStepForm(FormStep.ChangeAssignedProspect)
            }
            // onShowEditProspect={() => setStepForm(FormStep.EditProspect)}
            onShowEditProspect={(prospectType, idx) => {
              console.log("prospectType on show edirt propscet", prospectType);

              setIndex(idx);

              if (prospectType === ProspectType.Prospect) {
                setStepForm(FormStep.EditProspectFromCosigners);
              } else if (prospectType === ProspectType.Cosigner) {
                setStepForm(FormStep.EditCosignerFromCosigners);
              }
            }}
            onClose={onDismiss}
            onDismiss={() => setStepForm(FormStep.None)}
            onCosignersUpdate={(cosigners: IProspect[]) => {
              // updateCosigners(cosigners)
            }}
          />
        );

      default:
        console.log(`default switch`);
    }
  };

  const handleDropdownChange = (e, item) => {
    formik.setValues(
      {
        ...formik.values,
        [e.target.id]: item.key,
      },
      true
    );
  };

  const handleToggle = () => {
    formik.setFieldValue("hasOfferBeenSent", !formik.values.hasOfferBeenSent);
  };

  const handleSetStatus = (status: number) => {
    const updatedDeal = { ...formik.values, status: status };
    const items = [updatedDeal];
    dispatch<any>(handleUpdateDeal(items, () => {}));
  };

  const handleDelete = () => {
    setIsConfirmBoxOpen(true);
  };

  const handleToggleFavourite = (id: number, action: string) => {
    const favouriteAction =
      action === "add" ? FavouriteAction.add : FavouriteAction.remove;
    dispatch<any>(
      handleUpdateFavourite(
        [{ endpoint: "deal", id: id }],
        () => {},
        favouriteAction
      )
    );
  };

  const _handleDeleteDeal = () => {
    onDismiss();
    dispatch<any>(handleDeleteDeal([selectedDeal.id]));
  };

  return (
    <>
      {_renderDialog()}
      {isConfirmBoxOpen && (
        <ConfirmDialog
          loading={loading}
          description="Please confirm deletion of prospect"
          title="Confirm delete prospect"
          isOpen={isConfirmBoxOpen}
          onAction={_handleDeleteDeal}
          onDismiss={() => setIsConfirmBoxOpen(false)}
        />
      )}
      <Modal
        isOpen={true}
        onDismiss={onDismiss}
        isBlocking={true}
        containerClassName={contentStyles.container}
      >
        <div className={contentStyles.header}>
          <CustomFontText>Edit Deal</CustomFontText>
          <IconButton
            styles={iconButtonStyles}
            iconProps={cancelIcon}
            ariaLabel="Close popup modal"
            onClick={onDismiss}
          />
        </div>
        <div className={contentStyles.body}>
          <div
            style={{ fontSize: 12, color: colourGrey.color, marginBottom: 10 }}
          >
            last modified in
            {moment(selectedDeal.updatedAt).format("MMM DD YYYY")} by ,
            <span style={{ color: colourDefault.color }} onClick={() => {}}>
              view employee
            </span>
          </div>
          <span className={contentStyles.label}>Deal Create Date</span>
          <TextField
            id="createdAt"
            name="createdAt"
            // label="Assigned Prospect"
            value={
              selectedDeal
                ? moment(selectedDeal.createdAt).format("MMM DD YYYY")
                : ""
            }
            disabled
          />
          <Stack>
            <Dropdown
              id="setDealType"
              label="Set Deal Type"
              selectedKey={formik.values.setDealType}
              onChange={handleDropdownChange}
              placeholder="any..."
              options={dealTypes}
              // styles={dropdownStyles}
              onBlur={formik.handleBlur}
              errorMessage={
                formik.touched.setDealType && formik.errors.setDealType
                  ? formik.errors.setDealType.toString()
                  : null
              }
            />
            <Dropdown
              id="dealStatus"
              label="Deal Status"
              selectedKey={formik.values.dealStatus}
              onChange={handleDropdownChange}
              placeholder="any..."
              options={dealStatuses}
              // styles={dropdownStyles}
              onBlur={formik.handleBlur}
              errorMessage={
                formik.touched.dealStatus && formik.errors.dealStatus
                  ? formik.errors.dealStatus.toString()
                  : null
              }
            />
            <span className={contentStyles.label}>Assigned Prospect</span>
            <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 10 }}>
              {/*<Stack.Item grow={3}>*/}

              <div style={{ flex: 4 }}>
                <TextField
                  id="assignedProspect"
                  name="assignedProspect"
                  // label="Assigned Prospect"
                  value={
                    selectedProspect
                      ? `${selectedProspect?.firstName} ${selectedProspect?.lastName}, ${selectedProspect?.email} ,${selectedProspect?.phone}`
                      : ""
                  }
                  disabled
                  errorMessage={
                    formik.touched.assignedProspect &&
                    formik.errors.assignedProspect
                      ? formik.errors.assignedProspect.toString()
                      : null
                  }
                />
              </div>
              {/*</Stack.Item>*/}
              {/*<Stack.Item grow={1}>*/}
              {selectedProspect && (
                <DefaultButton
                  style={{ flex: 2 }}
                  disabled={!selectedProspect}
                  iconProps={{ iconName: "RedEye" }}
                  text="View Detail"
                  onClick={() => setStepForm(FormStep.EditProspect)}
                />
              )}
              {/*</Stack.Item>*/}
            </Stack>
            <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 10 }}>
              {selectedProspect ? (
                <PrimaryButton
                  style={{
                    marginTop: 10,
                  }}
                  // disabled={!formik.dirty || !formik.isValid}
                  // onRenderIcon={renderSpinner}
                  iconProps={{ iconName: "ReminderPerson" }}
                  text={"Change Assigned Prospect"}
                  onClick={() => {
                    setStepForm(FormStep.ChangeAssignedProspect);
                    setSelectedType(1);
                  }}
                />
              ) : (
                <DefaultButton
                  text={"Assign Assigned Prospect"}
                  style={{
                    marginTop: 10,
                  }}
                  // disabled={!formik.dirty || !formik.isValid}
                  // onRenderIcon={renderSpinner}
                  iconProps={{ iconName: "ReminderPerson" }}
                  onClick={() => {
                    setStepForm(FormStep.ChangeAssignedProspect);
                    setSelectedType(1);
                  }}
                />
              )}
            </Stack>
            <span className={contentStyles.label}>Assigned Vehicle</span>
            <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 10 }}>
              <div style={{ flex: 4 }}>
                <TextField
                  id="assignedVehicle"
                  name="assignedVehicle"
                  // label="Assigned Vehicle"
                  value={
                    selectedInventory
                      ? `${selectedInventory.make} ${selectedInventory.model}`
                      : ""
                  }
                  disabled
                  errorMessage={
                    formik.touched.assignedVehicle &&
                    formik.errors.assignedVehicle
                      ? formik.errors.assignedVehicle.toString()
                      : null
                  }
                />
              </div>
              {selectedInventory && (
                <DefaultButton
                  style={{ flex: 2 }}
                  disabled={!selectedInventory}
                  iconProps={{ iconName: "RedEye" }}
                  text={"View Detail"}
                  onClick={() => setStepForm(FormStep.EditInventory)}
                />
              )}
            </Stack>
            <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 10 }}>
              {selectedInventory ? (
                <PrimaryButton
                  style={{
                    marginTop: 10,
                  }}
                  // disabled={!formik.dirty || !formik.isValid}
                  // onRenderIcon={renderSpinner}
                  iconProps={{ iconName: "Car" }}
                  text={"Change Vehicle"}
                  onClick={() => setStepForm(FormStep.AssignVehicleToDeal)}
                />
              ) : (
                <DefaultButton
                  text="Assign Vehicle to Deal"
                  style={{
                    marginTop: 10,
                  }}
                  // disabled={!formik.dirty || !formik.isValid}
                  // onRenderIcon={renderSpinner}
                  iconProps={{ iconName: "Car" }}
                  onClick={() => setStepForm(FormStep.AssignVehicleToDeal)}
                />
              )}
            </Stack>
            <div style={{ marginTop: 20, marginBottom: 20 }}>
              <ListItem
                onClick={() => {
                  setStepForm(FormStep.Cosigners);
                  setSelectedType(2);
                }}
                subTitles={
                  formik.values.cosigners[0] === null
                    ? ["No co-signatories assigned"]
                    : [`${formik.values.cosigners[0].firstName} ${formik.values.cosigners[0].lastName}`]
                }

                title="Co-Signers"
                optional
              />
            </div>
            <Stack.Item>
              <Stack>
                <Toggle
                  id="hasOfferBeenSent"
                  checked={formik.values.hasOfferBeenSent}
                  onBlur={formik.handleBlur}
                  onChange={handleToggle}
                  label="The offer has been sent"
                  inlineLabel
                  onText="send"
                  offText="not send"
                />
              </Stack>
            </Stack.Item>
          </Stack>
        </div>
        <div className={contentStyles.footer}>
          <Stack
            tokens={{ childrenGap: 5 }}
            horizontal
            horizontalAlign="space-between"
          >
            <div style={{ width: "100%" }}>
              <DealEditAdditionalActionsDropdown
                modulePermissions={getModulePermissions(
                  user.auths || [],
                  "Deals"
                )}
                handleAddToFavourite={() =>
                  handleToggleFavourite(selectedDeal.id, FavouriteAction.add)
                }
                handleRemoveFromFavourite={() =>
                  handleToggleFavourite(selectedDeal.id, FavouriteAction.remove)
                }
                handleSetStatus={handleSetStatus}
                handleDelete={handleDelete}
              />
            </div>

            <DefaultButton text="Close" onClick={onDismiss} />
            <PrimaryButton
              disabled={!formik.dirty || !formik.isValid}
              onRenderIcon={renderSpinner}
              text="Save changes"
              onClick={handleUpdate}
              // loading={loading}
              styles={{
                root: {
                  width: 200,
                },
              }}
            />
          </Stack>
        </div>
      </Modal>
    </>
  );
};
